import { useT } from '../i18n';
import { ROUTES } from '../lib/router';
import { formatHa, formatSqm } from '../lib/geo';
import { judetName } from '../domain/judete';
import type { OwnerContact, ParcelDetails, ParcelSelection, SubmissionFiles } from '../domain/types';
import { isCertificateMissing, parseDecimal, type Errors } from '../lib/validation';
import { Field } from './Field';
import './StepForm.css';

interface StepContactProps {
  contact: OwnerContact;
  details: ParcelDetails;
  selection: ParcelSelection;
  files: SubmissionFiles;
  errors: Errors;
  onContactChange: (update: Partial<OwnerContact>) => void;
  /** Takes the owner back to step 2 to attach the certificate they skipped. */
  onGoAttachCertificate: () => void;
}

export function StepContact({
  contact,
  details,
  selection,
  files,
  errors,
  onContactChange,
  onGoAttachCertificate,
}: StepContactProps) {
  const t = useT();

  const area = parseDecimal(details.areaSqm);
  const fileCount = (files.certificate ? 1 : 0) + files.additional.length;
  const location = [details.localitate, details.judet ? judetName(details.judet) : '']
    .filter(Boolean)
    .join(', ');
  const certificateMissing = isCertificateMissing(files);

  return (
    /*
     * Wide, like step 2. The summary and the missing-certificate notice are
     * full-width blocks of their own; held in the 660px form column they were
     * a narrow ribbon down the left of the page while the map above them ran
     * the full width of the shell.
     */
    <div className="step-form step-form--wide">
      <header className="step-form__intro">
        <h2>{t.step3.heading}</h2>
        <p className="lede">{t.step3.lede}</p>
      </header>

      {/*
        "Ce primiți" is NOT here. It listed what the owner receives directly
        above the name and phone fields, which put a promise between the reader
        and the two inputs they were hesitating over. It now closes the
        acknowledgement instead — see Acknowledgement.tsx — where it is read
        after the commitment rather than as an argument for making it.
      */}
      <div className="step-form__stack">
        {/*
          The contact fields and the summary sit side by side. What the owner is
          about to send and who is sending it are one act, and pairing them puts
          the parcel figures in view while the last three fields are filled —
          rather than below the fold, where the summary was only read after the
          decision it is meant to inform.

          The two columns collapse to one below 900px; a label/value table and a
          form field cannot both hold a half-column on a narrow screen.
        */}
        <div className="step-contact__top">
        <div className="step-form__fields">
        <Field name="name" label={t.step3.nameLabel} error={errors['name']}>
          {(props) => (
            <input
              {...props}
              className="input"
              type="text"
              autoComplete="name"
              placeholder={t.step3.namePlaceholder}
              value={contact.name}
              onChange={(e) => onContactChange({ name: e.target.value })}
            />
          )}
        </Field>

        <Field name="email" label={t.step3.emailLabel} error={errors['email']}>
          {(props) => (
            <input
              {...props}
              className="input"
              type="email"
              inputMode="email"
              autoComplete="email"
              // An email is not prose; red squiggles under it only cause doubt.
              spellCheck={false}
              autoCapitalize="off"
              placeholder={t.step3.emailPlaceholder}
              value={contact.email}
              onChange={(e) => onContactChange({ email: e.target.value })}
            />
          )}
        </Field>

        <Field name="phone" label={t.step3.phoneLabel} help={t.step3.phoneHelp} error={errors['phone']}>
          {(props) => (
            <input
              {...props}
              className="input"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              spellCheck={false}
              placeholder={t.step3.phonePlaceholder}
              value={contact.phone}
              onChange={(e) => onContactChange({ phone: e.target.value })}
            />
          )}
        </Field>
        </div>

        {/*
          The heading sits OUTSIDE the panel, so it titles the block the way a
          field label titles its input. It carries the same size, leading and
          weight as .field__label, and the gap below it is the same
          --space-3xs, so "Rezumatul solicitării" lands on the same baseline as
          "Nume și prenume" and the panel top lines up with the first input.
        */}
        <section className="summary-block" aria-labelledby="summary-heading">
          <h3 className="summary__heading" id="summary-heading">
            {t.step3.summaryHeading}
          </h3>
          <dl className="summary summary__list">
            <div className="summary__row">
              <dt className="summary__key">{t.step3.summaryArea}</dt>
              <dd className="summary__value">
                {area === null
                  ? '—'
                  : `${t.step1.areaSqm(formatSqm(area))} · ${t.step1.areaHa(formatHa(area))}`}
                {/* How the figure was captured qualifies the area, so it sits
                    under the number, not under the address. */}
                <span className="summary__note">
                  {selection.method === 'draw' ? t.step3.summaryMethodDraw : t.step3.summaryMethodManual}
                </span>
              </dd>
            </div>
            <div className="summary__row">
              <dt className="summary__key">{t.step3.summaryLocation}</dt>
              <dd className="summary__value">{location || '—'}</dd>
            </div>
            <div className="summary__row">
              <dt className="summary__key">{t.step3.summaryFiles}</dt>
              <dd className="summary__value">{t.step3.summaryFilesCount(fileCount)}</dd>
            </div>
          </dl>
        </section>
        </div>

        {/*
          A notice, not an error. The submission is valid without a certificate,
          so this uses `role="note"` rather than `alert`, states what the owner
          gives up, and offers the way back — it must never read as a blocker.
        */}
        {certificateMissing && (
          <aside className="notice" role="note">
            <h3 className="notice__heading">{t.step2.certificateMissingHeading}</h3>
            <p className="notice__body">{t.step2.certificateMissingBody}</p>
            <button type="button" className="btn btn--quiet notice__action" onClick={onGoAttachCertificate}>
              {t.step2.certificateMissingAction}
            </button>
          </aside>
        )}

        {/* The whole label is the hit target, checkbox and legal text together. */}
        <label className={errors['consent'] ? 'consent consent--error' : 'consent'}>
          <input
            className="consent__box"
            type="checkbox"
            data-field="consent"
            checked={contact.consent}
            aria-invalid={Boolean(errors['consent'])}
            aria-describedby={errors['consent'] ? 'consent-error' : undefined}
            onChange={(e) => onContactChange({ consent: e.target.checked })}
          />
          <span className="consent__text">
            {t.step3.consentText}{' '}
            {/*
              New tab, deliberately. This link sits inside the consent checkbox
              on the last step, next to a form the owner has already filled in
              and a certificate they have already uploaded. Taking them off the
              page to read a legal document at that exact moment is the surest
              way to lose the submission.
            */}
            <a href={ROUTES.privacy} target="_blank" rel="noopener noreferrer">
              {t.step3.consentLink}
            </a>
          </span>
        </label>

        {errors['consent'] && (
          <p className="field__error" id="consent-error">
            <span className="visually-hidden">{t.validation.fieldError}</span>
            <span aria-hidden="true">↳</span>
            {errors['consent']}
          </p>
        )}
      </div>
    </div>
  );
}
